import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  CircularProgress,
  Box,
  Divider,
  Paper,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import EmailIcon from "@mui/icons-material/Email";
import VerifiedIcon from "@mui/icons-material/Verified";
import {
  sendProposalOtp,
  verifyProposalOtp,
} from "../../apis/apisList/userApi";
import { toast } from "react-toastify";

const ProposalAccessDialog = ({
  open,
  onClose,
  loginRedirectUrl,
  projectId,
}) => {
  const [step, setStep] = useState("choose"); // choose | verifyOtp | verified
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState(null);

  // Mock API calls
  const sendOtp = async () => {
    setLoading(true);
    setOtp("");
    try {
      await sendProposalOtp(projectId);
      setLoading(false);
      setStep("verifyOtp");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Internal issue");
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    setLoading(true);
    try {
      let res = await verifyProposalOtp(projectId, { otp });
      setToken(res.token);
      setLoading(false);
      setStep("verified");
    } catch (error) {
      console.log(">>verifyOtp>>>", error);
      toast.error(error?.response?.data?.message || "Internal issue");
      setLoading(false);
    }
  };

  const handleLogin = () => {
    window.location.href = `/login?redirect=${loginRedirectUrl}`;
  };
  const handlePdfView = () => {
    if (!token) {
      toast.info("No token found for download pdf");
      return;
    }
    window.location.href = `/proposal?id=${projectId}&token=${token}`;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <LockIcon color="primary" />
          <Typography variant="h6">Secure Proposal Access</Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Step 1 - Choose access method */}
        {step === "choose" && (
          <Box display="flex" flexDirection="column" gap={3}>
            <Typography variant="body1">
              Choose how you’d like to securely access your proposal:
            </Typography>

            <Paper elevation={2} sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                🔑 Already have an account?
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Login to your account to view and download your proposal.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={handleLogin}
              >
                Login & Continue
              </Button>
            </Paper>

            <Divider>OR</Divider>

            <Paper elevation={2} sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                <EmailIcon fontSize="small" sx={{ mr: 1 }} />
                Access with One-Time Password
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                We’ll send a one-time code to the email associated with this
                project.
              </Typography>
              <Button
                variant="outlined"
                fullWidth
                onClick={sendOtp}
                disabled={loading}
              >
                {loading ? <CircularProgress size={20} /> : "Send OTP"}
              </Button>
            </Paper>
          </Box>
        )}

        {/* Step 2 - Verify OTP */}
        {step === "verifyOtp" && (
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="body1" gutterBottom>
              ✅ An OTP has been sent to the email associated with this project.
            </Typography>
            <TextField
              fullWidth
              label="Enter One-Time Password"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              margin="normal"
            />
            <Button
              variant="contained"
              color="primary"
              fullWidth
              disabled={loading || !otp}
              onClick={verifyOtp}
            >
              {loading ? <CircularProgress size={20} /> : "Verify OTP"}
            </Button>
            <Button
              variant="text"
              color="secondary"
              onClick={sendOtp}
              disabled={loading}
            >
              Resend OTP
            </Button>
          </Box>
        )}

        {/* Step 3 - Verified */}
        {step === "verified" && (
          <Box textAlign="center" py={3}>
            <VerifiedIcon color="success" sx={{ fontSize: 50 }} />
            <Typography variant="h6" gutterBottom>
              Access Granted
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              You can now securely download your proposal PDF.
            </Typography>
            <Button
              variant="contained"
              color="success"
              size="large"
              onClick={handlePdfView}
            >
              Download Proposal PDF
            </Button>
          </Box>
        )}
      </DialogContent>

      {step !== "verified" && (
        <DialogActions>
          <Button onClick={onClose} color="inherit">
            Cancel
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ProposalAccessDialog;
